import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core'
import { Add, Delete, Edit } from '@material-ui/icons'

import { FieldTypes } from '../../utils/constants'
import useMonthlyReportsApi from '../../hooks/monthlyReports/useMonthlyReportsApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import useToastContext from '../../hooks/useToastContext'
import { notificationTypes } from '../../contexts/toastContext'
import ListHeader from '../common/ListHeader'
import Form from '../common/Form'
import { TextField } from '../common/FormFields'
import CustomTable from '../customTable/CustomTable'

const emptyForm = {
  name: '',
  buildings: [],
  recipientEmails: [],
  dayOfMonth: 1,
  active: true,
}

export default function MonthlyReportsList() {
  const addToast = useToastContext()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [emailDraft, setEmailDraft] = useState('')

  const [{ configsState }, methods] = useMonthlyReportsApi()
  const [{ buildingsState }, buildingMethods] = useBuildingsApi()

  useEffect(() => {
    methods.getConfigs()
    buildingMethods.getBuildings(true)
  }, [])

  const openCreate = () => {
    setForm(emptyForm)
    setEditingId(null)
    setEmailDraft('')
    setDialogOpen(true)
  }

  const openEdit = (config) => {
    const buildings = (buildingsState.buildings || []).filter((b) =>
      (config.buildingIds || []).includes(b.id)
    )
    setForm({
      name: config.name,
      buildings,
      recipientEmails: config.recipientEmails || [],
      dayOfMonth: config.dayOfMonth,
      active: config.active,
    })
    setEditingId(config.id)
    setEmailDraft('')
    setDialogOpen(true)
  }

  const addEmail = () => {
    const trimmed = emailDraft.trim().toLowerCase()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return
    if (form.recipientEmails.includes(trimmed)) { setEmailDraft(''); return }
    setForm((f) => ({ ...f, recipientEmails: [...f.recipientEmails, trimmed] }))
    setEmailDraft('')
  }

  const removeEmail = (email) => {
    setForm((f) => ({ ...f, recipientEmails: f.recipientEmails.filter((e) => e !== email) }))
  }

  const handleSubmit = async (values) => {
    if (form.recipientEmails.length === 0) {
      addToast(notificationTypes.error, 'Agregue al menos un destinatario')
      return
    }
    const dayOfMonth = Number(values.dayOfMonth)
    if (!Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 28) {
      addToast(notificationTypes.error, 'El día del mes debe estar entre 1 y 28')
      return
    }
    const payload = {
      ...values,
      buildingIds: values.buildings.map((b) => b.id),
      recipientEmails: form.recipientEmails,
      dayOfMonth: Number(values.dayOfMonth),
    }
    delete payload.buildings
    try {
      if (editingId) {
        await methods.updateConfig(editingId, payload)
      } else {
        await methods.createConfig(payload)
      }
      setDialogOpen(false)
    } catch (e) {}
  }

  const buildingName = (id) =>
    (buildingsState.buildings || []).find((b) => b.id === id)?.name || id

  const columns = [
    { id: 'name', label: 'Nombre', minWidth: 150, format: (c) => c.name },
    {
      id: 'buildings',
      label: 'Edificios',
      minWidth: 180,
      format: (c) =>
        buildingsState.buildings
          ? (c.buildingIds || []).map((id) => (
              <Chip key={id} size="small" label={buildingName(id)} style={{ margin: 2 }} />
            ))
          : null,
    },
    {
      id: 'emails',
      label: 'Destinatarios',
      minWidth: 200,
      format: (c) =>
        (c.recipientEmails || []).map((e) => (
          <Chip key={e} size="small" label={e} style={{ margin: 2 }} />
        )),
    },
    { id: 'dayOfMonth', label: 'Día del mes', minWidth: 80, format: (c) => c.dayOfMonth },
    { id: 'active', label: 'Activo', minWidth: 60, format: (c) => (c.active ? 'Sí' : 'No') },
    { id: '', label: '', minWidth: 5 },
  ]

  const tableOptions = (config) => [
    { key: 'edit', label: 'Editar', onClick: () => openEdit(config) },
    {
      key: 'delete',
      label: 'Eliminar',
      onClick: () => {
        if (window.confirm(`¿Eliminar el reporte "${config.name}"? Esta acción no se puede deshacer.`)) {
          methods.deleteConfig(config.id)
        }
      },
    },
  ]

  const fields = [
    {
      type: FieldTypes.TextField,
      label: 'Nombre',
      hint: 'Nombre del reporte',
      key: 'name',
      withMargin: true,
      errorMessage: 'El nombre es requerido',
    },
    {
      type: FieldTypes.Autocomplete,
      label: 'Edificios',
      hint: 'Seleccione uno o más edificios',
      key: 'buildings',
      options: buildingsState.buildings || [],
      multiple: true,
      freeSolo: false,
      withMargin: true,
      onChange: (value) => setForm((f) => ({ ...f, buildings: value })),
      errorMessage: 'Seleccione al menos un edificio',
    },
    {
      type: FieldTypes.Custom,
      key: 'emailSection',
      render: () => (
        <Box mb={2}>
          <Box display="flex" alignItems="flex-end" style={{ gap: 8 }}>
            <Box flex={1}>
              <TextField
                field={{ label: 'Destinatarios', hint: 'email@ejemplo.com', key: 'emailDraft' }}
                values={{ emailDraft }}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail() } }}
              />
            </Box>
            <IconButton onClick={addEmail} color="primary" style={{ marginBottom: 12 }}>
              <Add />
            </IconButton>
          </Box>
          <Box mt={1} display="flex" flexWrap="wrap">
            {form.recipientEmails.map((e) => (
              <Chip key={e} label={e} onDelete={() => removeEmail(e)} style={{ margin: 2 }} />
            ))}
          </Box>
        </Box>
      ),
    },
    {
      type: FieldTypes.TextField,
      label: 'Día del mes (1–28)',
      hint: '1',
      key: 'dayOfMonth',
      numeric: true,
      withMargin: true,
      errorMessage: 'El día es requerido',
    },
    {
      type: FieldTypes.Switch,
      label: 'Activo',
      key: 'active',
      withMargin: true,
    },
  ]

  return (
    <>
      <ListHeader
        bigTitle="Reportes mensuales"
        canCreate={true}
        createText="Nuevo"
        createRedirect={openCreate}
      />

      <CustomTable
        loading={configsState.isLoading}
        columns={columns}
        data={configsState.configs || []}
        options={tableOptions}
        serverPagination={false}
        getData={methods.getConfigs}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar reporte mensual' : 'Nuevo reporte mensual'}
        </DialogTitle>
        <DialogContent>
          <Form
            values={form}
            setValues={setForm}
            fields={fields}
            handleSubmit={handleSubmit}
            handleCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
